import type { User } from "../../types";
import type { CardQR, Person, ProveedorPersonFormData } from "../../types/person";
import { apiClient } from "../core/apiClient";


interface getPersonResult {
  data: Person[];
  pagination: {
    currentPage: number,
    totalPages: number,
    totalItems: number,
    itemsPerPage: number,
    itemsInCurrentPage: number,
    hasNextPage: boolean,
    hasPreviousPage: boolean,
    nextPage: string | null,
    previousPage: string | null
  }
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  refetch: () => void;
}

interface postPersonResult {
  user: {
    employeeNo: string,
    name: string,
    userType: string,
    doorRight: string
  }
}

interface QrListResult {
  numero_persona: string,
  total_cards: number,
  cards: CardQR[],
  latest_card: {
    personas_qr_id: number,
    numero_tarjeta: string,
    fecha_visita: string,
    created_at: string
  },
  timestamp: string
}

export class PersonService {
  async getVisitors(params?: { name_persona?: string; page?: number; status_persona_id?: number, tipo_persona_id?: string }): Promise<getPersonResult> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';

    const endpoint = `/personas${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<getPersonResult>(endpoint, { timeout: 50000 });
    return response.data;
  }

  async createPerson(namePerson: string, banco_s3: boolean): Promise<{ success: Boolean, data?: postPersonResult, error?: string }> {
    try {
      const response = await apiClient.post<postPersonResult>('/personas/add', { "name": namePerson, "banco_s3": banco_s3 }, { timeout: 30000 });
      if (response.state) {
        return {
          success: response.state,
          data: response.data
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al sincronizar facial:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async addProveedorPerson(proveedorData: ProveedorPersonFormData): Promise<{ success: Boolean, error?: string }> {

    try {
      const response = await apiClient.post<postPersonResult>('/personas/add-type-proveedor', proveedorData, { timeout: 50000 });
      if (response.state) {
        return {
          success: response.state,
        };
      }
      return {
        success: false
      };

    } catch (error) {
      console.error('Error al sincronizar facial:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async addPhotoPerson(userId: string, file: File): Promise<Boolean> {
    const response = await apiClient.uploadFile<{ avatarUrl: string }>(`/personas/add-photo/${userId}`, file, { timeout: 50000 });
    return response.state;
  }

  async syncFacial(facialId: string): Promise<{ success: Boolean, error?: string }> {
    try {
      const response = await apiClient.get<getPersonResult>(`/sincronizar-facial/${facialId}`, { timeout: 50000 });
      return {
        success: response.state
      };
    } catch (error) {
      console.error('Error al sincronizar facial:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async syncLastUserFacial(facialId: string): Promise<{ success: Boolean, error?: string }> {
    try {
      const response = await apiClient.get<getPersonResult>(`/sincronizar-facial/last/${facialId}`, { timeout: 50000 });
      return {
        success: response.state
      };
    } catch (error) {
      console.error('Error al sincronizar facial:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updatePersonStatus(personId: string, statusId: number): Promise<void> {
    const response = await apiClient.post(`/personas/change-status`, {
      employeeNo: personId,
      status: statusId  // 1 = Activo, 2 = Inactivo
    });
    return response.data;
  }

  async addPersonQR(employeeNo: string, fecha_visita: string): Promise<void> {

    const response = await apiClient.post(`/personas/agregar-qr`, {
      employeeNo: employeeNo,
      fecha_visita: fecha_visita
    }, { timeout: 30000 });
    return response.data;
  }

  async deletePersonQR(qr_id: number): Promise<void> {
    const response = await apiClient.post(`/personas/remove-qr`, {
      personas_qr_id: qr_id,
    }, { timeout: 50000 });
    return response.data;
  }

  async getQRListPerson(employeeNo: string): Promise<QrListResult> {

    const response = await apiClient.get(`/personas/qr/${employeeNo}`);
    return response.data;
  }

  async getUsers(params?: { page?: number; limit?: number; role?: string }): Promise<User[]> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const endpoint = `/users${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<User[]>(endpoint, { timeout: 50000 });
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<User>(`/users/${id}`, { timeout: 50000 });
    return response.data;
  }

  async createUser(userData: Omit<User, 'id'>): Promise<User> {
    const response = await apiClient.post<User>('/users', userData, { timeout: 50000 });
    return response.data;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await apiClient.put<User>(`/users/${id}`, userData, { timeout: 50000 });
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  }

  async uploadAvatar(userId: string, file: File): Promise<{ avatarUrl: string }> {
    const response = await apiClient.uploadFile<{ avatarUrl: string }>(`/users/${userId}/avatar`, file);
    return response.data;
  }
  
  async addVisita(employeeId: string, visitPerson: string, authPerson: string, areaToVisit: string, pisoToVisit: string, visitPurpose?: string): Promise<void> {
    await apiClient.post(`/visitas/` , {employee_id: employeeId, visit_person: visitPerson, auth_person: authPerson, area_to_visit: areaToVisit, piso_to_visit: pisoToVisit, visit_purpose: visitPurpose});
  }
}

export const personService = new PersonService();